import { memo, useState, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { MessengerMessage } from '@/api/client';
import { ContextMenu, type ContextMenuItem } from '@/components/ui/ContextMenu/ContextMenu';
import { FaMessengerIcon } from '@/components/ui/Icons';
import { MessengerMessageRow } from '@/components/Messenger/shared/messages/MessengerMessageRow';
import { BubbleShell } from '@/components/Messenger/shared/messages/BubbleShell';
import { DmMessageBubble } from '@/components/Messenger/shared/messages/DmMessageBubble';
import { MessageMeta } from '@/components/Messenger/shared/messages/MessageMeta';
import { MessengerMarkdownAgentExtended } from '@/components/Messenger/shared/markdown/MessengerMarkdownAgentExtended';
import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import {
  AGENT_ASSISTANT_FORWARD_LABEL,
  trackerAssistantMessageToForwardDraft,
} from '@/components/Messenger/agent/agentAssistantForwardDraft';
import { AssistantModelMetaFooter } from '@/components/Messenger/agent/AssistantModelMeta';
import type { AgentChatModelOption } from '@/components/Messenger/agent/agentChatSharedTypes';
import { isValidAgentModelSlot } from '@/components/Messenger/agent/assistantChatMessageMetadata';
import { areTrackerChatMessagesEqual } from '@/components/Messenger/agent/agentChatMessageStability';
import { assistantUiTextFromParts } from '@/components/Messenger/agent/agentMessageRender/assistantUiText';
import { useToast } from '@/contexts/ToastContext';
import { copyPlainTextToClipboard } from '@/lib/copyPlainTextToClipboard';
import {
  captureDocumentSelection,
  getMessageCopyMenuLabel,
  getSelectedPlainTextWithinContainer,
  resolveMessageCopyText,
  scheduleRestoreDocumentSelection,
} from '@/components/Messenger/shared/messages/messageContextCopy';
import { MessengerDmFileAttachment } from '@/components/Messenger/shared/attachments/MessengerDmFileAttachment';
import { resolveAgentPartsFileTarget } from '@/components/Messenger/shared/messages/messengerAttachmentTarget';
import {
  buildAgentAttachmentContextMenuItems,
  messageContextCopyTextIcon,
} from '@/components/Messenger/shared/messages/messageContextAttachmentMenuItems';

type AgentAssistantMessageRowProps = {
  message: TrackerChatMessage;
  timelineIso: string;
  sameAsPrev: boolean;
  status: string;
  sendQuickMessage: (m: { text: string }) => void;
  wrapperClassName?: string;
  animateEnter?: boolean;
  onForward: (draft: MessengerMessage) => void;
  chatModels: AgentChatModelOption[];
};

export const AgentAssistantMessageRow = memo(function AgentAssistantMessageRow({
  message,
  timelineIso,
  sameAsPrev,
  status,
  sendQuickMessage,
  wrapperClassName,
  animateEnter = false,
  onForward,
  chatModels,
}: AgentAssistantMessageRowProps) {
  const { showError, showSuccess } = useToast();
  const [ctxPos, setCtxPos] = useState<{ x: number; y: number } | null>(null);
  const [ctxSelectedText, setCtxSelectedText] = useState('');

  const canForward = status === 'ready';

  const slot = message.metadata?.modelSlot;
  const modelLeft = useMemo(() => {
    if (isValidAgentModelSlot(slot)) {
      return <AssistantModelMetaFooter slot={slot} chatModels={chatModels} />;
    }
    return (
      <div className="chatAssistant__dmAssistantModelMeta" title={AGENT_ASSISTANT_FORWARD_LABEL}>
        <span className="chatAssistant__dmAssistantModelMetaBrand">{AGENT_ASSISTANT_FORWARD_LABEL}</span>
      </div>
    );
  }, [slot, chatModels]);

  const combinedText = useMemo(() => assistantUiTextFromParts(message.parts), [message.parts]);
  const fileTarget = useMemo(() => resolveAgentPartsFileTarget(message.parts), [message.parts]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const hasText = combinedText.trim().length > 0;
      const hasFile = fileTarget != null;
      const draft = canForward ? trackerAssistantMessageToForwardDraft(message) : null;
      if (!hasText && !draft && !hasFile) return;
      e.preventDefault();
      setCtxSelectedText(getSelectedPlainTextWithinContainer(e.currentTarget as Element));
      setCtxPos({ x: e.clientX, y: e.clientY });
    },
    [canForward, message, combinedText, fileTarget]
  );

  const closeMenu = useCallback(() => {
    setCtxPos(null);
    setCtxSelectedText('');
  }, []);

  const handleForwardClick = useCallback(() => {
    const draft = trackerAssistantMessageToForwardDraft(message);
    closeMenu();
    if (!draft) {
      showError('Нечего переслать в этом сообщении');
      return;
    }
    onForward(draft);
  }, [message, onForward, closeMenu, showError]);

  const copyPayload = resolveMessageCopyText(ctxSelectedText, combinedText);
  const showCopyText = copyPayload.length > 0;
  const copyMenuLabel = getMessageCopyMenuLabel(ctxSelectedText.trim().length > 0);

  const handleCopyClick = useCallback(() => {
    const savedSelection = captureDocumentSelection();
    void (async () => {
      const ok = await copyPlainTextToClipboard(copyPayload);
      if (ok) showSuccess('Текст скопирован');
      else showError('Не удалось скопировать текст');
      closeMenu();
      scheduleRestoreDocumentSelection(savedSelection);
    })();
  }, [copyPayload, showSuccess, showError, closeMenu]);

  const handleCopyMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  }, []);

  const attachmentMenuItems = useMemo(
    () =>
      buildAgentAttachmentContextMenuItems(fileTarget, { showSuccess, showError }, closeMenu),
    [fileTarget, showSuccess, showError, closeMenu],
  );

  const contextMenuItems = useMemo(() => {
    const items: ContextMenuItem[] = [];
    if (showCopyText) {
      items.push({
        label: copyMenuLabel,
        icon: messageContextCopyTextIcon(),
        onClick: handleCopyClick,
        onMouseDown: handleCopyMouseDown,
      });
    }
    items.push(...attachmentMenuItems);
    if (canForward) {
      items.push({
        label: 'Переслать',
        icon: <FaMessengerIcon icon="share" size={18} />,
        onClick: handleForwardClick,
      });
    }
    return items;
  }, [
    showCopyText,
    copyMenuLabel,
    handleCopyClick,
    handleCopyMouseDown,
    attachmentMenuItems,
    canForward,
    handleForwardClick,
  ]);

  const timeLabel = format(new Date(timelineIso), 'HH:mm', { locale: ru });

  return (
    <div
      className="directChatView__msgSlot"
      data-dm-msg={message.id}
      onContextMenu={handleContextMenu}
    >
      <MessengerMessageRow
        alignment="assistant"
        sameAsPrev={sameAsPrev}
        animateEnter={animateEnter}
        wrapperClassName={wrapperClassName}
      >
        <DmMessageBubble variant="peer" peerSenderName={null}>
          <BubbleShell
            content={
              <>
                {fileTarget?.kind === 'file' ? (
                  <div className="chatAssistant__dmAttachmentBlock">
                    <MessengerDmFileAttachment
                      href={fileTarget.url}
                      fileName={fileTarget.fileName}
                      isOwn={false}
                    />
                  </div>
                ) : null}
                <div className="chatAssistant__dmTextWithMeta">
                  <div
                    className={[
                      'chatAssistant__dmTextWithMetaBody',
                      'chatAssistant__agentAssistantDmShellBody',
                    ].join(' ')}
                  >
                    <div className="chatAssistant__dmPeerBody">
                      <MessengerMarkdownAgentExtended
                        messageId={message.id}
                        rawText={combinedText}
                        status={status}
                        sendMessage={sendQuickMessage}
                        bubbleTailMetaLayout={false}
                      />
                    </div>
                    <div
                      className="chatAssistant__agentAssistantDmMetaStrip"
                      aria-label="Тип модели и время"
                    >
                      <div className="chatAssistant__agentAssistantDmMetaStripStart">{modelLeft}</div>
                      <span className="chatAssistant__dmTextWithMetaMeta chatAssistant__dmTextWithMetaMeta_asStripEnd">
                        <MessageMeta
                          timeLabel={timeLabel}
                          showReadReceipts={false}
                          isOwn={false}
                          edited={false}
                          inline
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </>
            }
          />
        </DmMessageBubble>
      </MessengerMessageRow>
      {contextMenuItems.length > 0 ? (
        <ContextMenu
          isOpen={ctxPos !== null}
          onClose={closeMenu}
          anchorPosition={ctxPos}
          items={contextMenuItems}
        />
      ) : null}
    </div>
  );
}, areAssistantMessageRowPropsEqual);

function areAssistantMessageRowPropsEqual(
  prev: AgentAssistantMessageRowProps,
  next: AgentAssistantMessageRowProps
): boolean {
  if (prev.sameAsPrev !== next.sameAsPrev) return false;
  if (prev.timelineIso !== next.timelineIso) return false;
  if (prev.status !== next.status) return false;
  if (prev.animateEnter !== next.animateEnter) return false;
  if (prev.wrapperClassName !== next.wrapperClassName) return false;
  if (prev.sendQuickMessage !== next.sendQuickMessage) return false;
  if (prev.onForward !== next.onForward) return false;
  if (prev.chatModels !== next.chatModels) return false;
  if (prev.message === next.message) return true;
  return areTrackerChatMessagesEqual(prev.message, next.message);
}
